/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { produce } from "immer";
import { actionFactory, ActionUnion, payloadAction, simpleAction } from "reductser";

/* -------------------------------------------------- */
/*               Action Types  & Creator              */
/* -------------------------------------------------- */

const actionSearch = {
  searchModalOpened: payloadAction<{ modalType: "command" | "search"; usedHotkey?: boolean }>(),
  searchModalClosed: payloadAction<{ usedHotkey?: boolean }>(),
  // change focused result index
  incrementFocusedResult: simpleAction(),
  decrementFocusedResult: simpleAction(),
  // set focused result index
  setFocusedResult: payloadAction<number>(),
  gotToFocused: simpleAction(),
};

export const searchActions = actionFactory(actionSearch, "SEARCH");

type SearchAction = ActionUnion<typeof searchActions>;

/* -------------------------------------------------- */
/*                        State                       */
/* -------------------------------------------------- */

export interface SearchState {
  modalOpen: false | "command" | "search";
  focusedResultIndex: number;
  goToFocused: boolean;
}

const getInitialState = (): SearchState => ({
  modalOpen: false,
  focusedResultIndex: 1,
  goToFocused: false,
});

/* -------------------------------------------------- */
/*                       Reducer                      */
/* -------------------------------------------------- */

const searchReducer = (state: SearchState = getInitialState(), action: SearchAction): SearchState =>
  produce(state, (draftState) => {
    if (action.reducer === "SEARCH") {
      switch (action.type) {
        case "searchModalOpened":
          draftState.modalOpen = action.payload.modalType;
          draftState.goToFocused = false;
          break;
        case "searchModalClosed":
          draftState.modalOpen = false;
          break;
        case "incrementFocusedResult":
          draftState.focusedResultIndex = Math.min(draftState.focusedResultIndex + 1, 18);
          break;
        case "decrementFocusedResult":
          draftState.focusedResultIndex = Math.max(draftState.focusedResultIndex - 1, 1);
          break;
        case "setFocusedResult":
          draftState.focusedResultIndex = action.payload;
          break;
        case "gotToFocused":
          draftState.goToFocused = true;
          break;
        default:
          break;
      }
    }
  });

export default searchReducer;
